<script setup lang="ts">
const route = useRoute()

// The signature host only serves its root (see 00.host.global.ts), so the
// installation guides live behind `?docs` instead of their own route.
const isDocs = computed(() => 'docs' in route.query)

watch(isDocs, () => window.scrollTo({ top: 0 }))

const { buildCommit, buildTime } = useRuntimeConfig().public

// Fixed locale + time zone so the SSR and client output match
const buildDate = new Intl.DateTimeFormat('it-IT', {
  dateStyle: 'short',
  timeStyle: 'short',
  timeZone: 'Europe/Rome',
}).format(new Date(buildTime))

// Subject carries the build so we know which version the report is about
const subject = `the no.j signature - [describe the problem] (build ${[buildCommit, buildDate].filter(Boolean).join(', ')})`
const helpHref = `mailto:hello@nodnod.studio?subject=${encodeURIComponent(subject)}`

useSeoMeta({
  robots: 'noindex, nofollow',
  title: () => isDocs.value ? 'Documentation – Email Signature Generator' : 'Email Signature Generator',
})
</script>

<template>
  <div class="bg-ivory flex flex-col min-h-screen">
    <header class="px-4 border-b border-black/10 flex h-$header-height items-center justify-center relative sm:px-6">
      <NuxtLink :to="{ path: route.path }" class="flex-none h-auto w-20 block relative z-2 lg:w-14.5" aria-label="The No. J Signature">
        <SvgoTheNoJ class="size-full" :font-controlled="false" />
      </NuxtLink>

      <NuxtLink
        :to="{ path: route.path, query: isDocs ? {} : { docs: null } }"
        class="typo-sans-1 c-[var(--primary-color)] px-4 motion-snug rounded-2.5 bg-[var(--negative-color)] flex gap-1.5 h-7.5 uppercase items-center right-4 absolute hover:bg-[color-mix(in_srgb,var(--negative-color)_80%,transparent)] sm:right-6"
      >
        <span :class="isDocs ? 'i-icon-signature' : 'i-icon-book-open-text'" class="size-4 icon" aria-hidden="true" />
        <!-- Icon only on phones, next to the centred logo -->
        <span class="max-sm:sr-only">{{ isDocs ? 'Generator' : 'Documentation' }}</span>
      </NuxtLink>
    </header>

    <Transition
      mode="out-in"
      enter-active-class="transition-opacity duration-250 ease"
      leave-active-class="transition-opacity duration-250 ease"
      enter-from-class="opacity-0"
      leave-to-class="opacity-0"
    >
      <SignatureDocs v-if="isDocs" class="flex-1" />
      <SignaturePanel v-else class="flex-1" />
    </Transition>

    <footer class="typo-sans-1 c-black/40 px-4 py-4 flex gap-4 justify-between sm:px-6">
      <span class="flex-1">Build {{ buildDate }}</span>
      <a :href="helpHref" class="hover:c-black">Need help?</a>
      <span class="text-right flex-1">{{ buildCommit }}</span>
    </footer>
  </div>
</template>
